/* eslint-disable */

import {useEffect, useState, useMemo, useCallback, useRef} from 'react'

export const useCustomDataTable = ({
  columns = [],
  records = [],
  pageSize = 10,
  onResetAll = () => {},
  onRowSelect = () => {},
  handleSorting = () => {},
  setSearchArry = () => {},
  setPageIndex = () => {},
  setIsSelectAll = () => {},
  isSelectAll = false,
  totalRecords = 0,
  initialSortConfig,
  initialRowSelection = [],
  firstPageIndex,
  disableSelectionPersistence = false,
}) => {
  /** @type {object} State for date range filters */
  const [dateRanges, setDateRanges] = useState({})
  /** @type {object} State for single date filters */
  const [singleDates, setSingleDates] = useState({})
  /** @type {string} Which date picker is open */
  const [openDatePicker, setOpenDatePicker] = useState('')
  /** @type {array} Currently selected rows */
  const [selectedRows, setSelectedRows] = useState(initialRowSelection || [])
  /** @type {boolean} Track if we started with select all but deselected some items */
  const [partialSelectAll, setPartialSelectAll] = useState(false)
  /** @type {Set} Track deselected items when in partial select all mode */
  const [deselectedItems, setDeselectedItems] = useState(new Set())
  /** @type {Set} Track globally selected items when in partial select all mode */
  const [globallySelectedRows, setGloballySelectedRows] = useState(new Set());
  /** @type {number} Track the original total when select all was first triggered */
  const [selectAllTotal, setSelectAllTotal] = useState(0);
  /** @type {boolean} Track if have filters */
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  /** @type {boolean} Track global select all */
  const [globalSelectAll, setGlobalSelectAll] = useState(false)
  /** @type {boolean} Track if have filters while select all */
  const [allFilteredSelected, setAllFilteredSelected] = useState(false)

  /** @type {object} Sort config (key, direction) */
  const [sortConfig, setSortConfig] = useState(() =>
    initialSortConfig && initialSortConfig.column
      ? {key: initialSortConfig.column, direction: initialSortConfig.direction || 'asc'}
      : {key: null, direction: 'desc'}
  )
// Common helper to get a row's unique identifier
const getRowId = row => row.rewardId ?? row.id ?? row.uuid ?? row.productId ?? row.storeId;

// Sync sortConfig with initialSortConfig if it changes (and user hasn't sorted)
  useEffect(() => {
    if (
      initialSortConfig &&
      (sortConfig.key !== initialSortConfig.column || sortConfig.direction !== initialSortConfig.direction)
    ) {
      setSortConfig({key: initialSortConfig.column, direction: initialSortConfig.direction || 'asc'})
    }
  }, [initialSortConfig])


  /** @type {object} Filter values by column */
  const [filter, setFilter] = useState(
    columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.name_of_db_column]: '',
      }),
      {}
    )
  )
  /** @type {NodeJS.Timeout|null} Debounce ref for search */
  const debounceRef = useRef(null)

  useEffect(() => {
    if (isSelectAll) {
      // Only set selectAllTotal if we're transitioning from false to true
      if (!globalSelectAll && !partialSelectAll) {
        setSelectAllTotal(totalRecords);
      }

      setGlobalSelectAll(true);
      setGloballySelectedRows(prev => {
        const updated = new Set(prev);
        records.forEach(record => {
          if (!Array.from(updated).some(existing => getRowId(existing) === getRowId(record))) {
            updated.add(record);
          }
        });
        return updated;
      });
      setSelectedRows(records);
      onRowSelect(records, true)
    } else if (!isSelectAll && globalSelectAll && !allFilteredSelected) {
      setGlobalSelectAll(false);
      setSelectedRows([]);
      onRowSelect([], false)
    } else if (!isSelectAll && globalSelectAll && !partialSelectAll) {
      setSelectAllTotal(0);
    }
  }, [isSelectAll, records, allFilteredSelected])

  useEffect(() => {
    // Determine if any filters are active
    const hasTextFilters = Object.values(filter).some(value => value !== '');
    const hasDateRangeFilters = Object.values(dateRanges).some(
      range => range?.startDate || range?.endDate
    );
    const hasSingleDateFilters = Object.values(singleDates).some(date => !!date);

    setHasActiveFilters(hasTextFilters || hasDateRangeFilters || hasSingleDateFilters);

  }, [filter, dateRanges, singleDates]);

  useEffect(() => {
    if (allFilteredSelected || globalSelectAll) {
      setGloballySelectedRows(prev => {
        const updated = new Set(prev);
        records.forEach(record => {
          const exists = Array.from(updated).some(
            existing => getRowId(existing) === getRowId(record)
          );
          if (!exists) updated.add(record);
        });
        return updated;
      });
      setSelectedRows(records);
    }
  }, [records, allFilteredSelected, globalSelectAll]);

  // Handle filter clearing when in selection modes
  useEffect(() => {
    if (!hasActiveFilters && allFilteredSelected) {
      // Filters were cleared while allFilteredSelected was true (no deselections yet)
      // Convert to globalSelectAll
      setIsSelectAll(true);
      setGlobalSelectAll(true);
      // Don't clear allFilteredSelected here - wait for isSelectAll to become true
    }
    
    if (!hasActiveFilters && partialSelectAll) {
      // Filters were cleared while in partialSelectAll mode (has deselections)
      // Stay in partialSelectAll mode but signal parent that we're in "select all" mode
      // Keep deselectedItems intact so deselected items remain deselected
      setIsSelectAll(true);
      setSelectAllTotal(totalRecords);
    }
    
    // Clear allFilteredSelected only after isSelectAll has been set to true by parent
    if (isSelectAll && allFilteredSelected) {
      setAllFilteredSelected(false);
    }
  }, [hasActiveFilters, allFilteredSelected, partialSelectAll, setIsSelectAll, isSelectAll, deselectedItems.size, totalRecords]);

  const prevTotalRecordsRef = useRef(totalRecords);
  // Update selectAllTotal when totalRecords changes while in partialSelectAll mode with active filters
  // This handles the case: select all -> apply filter -> count should reflect filtered total
  useEffect(() => {
    if (partialSelectAll && hasActiveFilters && prevTotalRecordsRef.current !== totalRecords) {
      setSelectAllTotal(totalRecords);
      // Clear deselectedItems since they may not exist in the filtered results
      setDeselectedItems(new Set());
    }
    prevTotalRecordsRef.current = totalRecords;
  }, [totalRecords, partialSelectAll, hasActiveFilters]);

  /**
   * Reset selections only
   */
  const handleResetSelections = useCallback(() => {
    setSelectedRows([])
    onRowSelect([], false)
    setGlobalSelectAll(false)
    setPartialSelectAll(false)
    setDeselectedItems(new Set())
    setGloballySelectedRows(new Set())
    setAllFilteredSelected(false)
    setIsSelectAll(false)
    setSelectAllTotal(0);
  }, [onRowSelect, setIsSelectAll])

  /**
   * Reset filters only
   */
  const handleResetFilters = useCallback(() => {
    // Call parent reset if provided (for external state)
    onResetAll()
    // Clear all local filter states
    setDateRanges({})
    setSingleDates({})
    setOpenDatePicker('')
    setFilter(
      columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.name_of_db_column]: '',
        }),
        {}
      )
    )
  }, [onResetAll, columns])

  /**
   * Reset all filters, sorting, and pagination
   */
  const handleResetAll = useCallback(() => {
    handleResetFilters()
    handleResetSelections()
  }, [handleResetFilters, handleResetSelections])

  /**
   * Memoized total pages for pagination
   */
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRecords / pageSize)), [totalRecords, pageSize])

  /**
   * Handle sorting by column
   * @param {string} key
   */
  const handleSort = useCallback(
    key => {
      let newDirection

      // If clicking a new column, start with desc
      if (sortConfig.key !== key) {
        newDirection = 'desc'
      }
      // If clicking the same column, toggle between desc and asc
      else {
        newDirection = sortConfig.direction === 'desc' ? 'asc' : 'desc'
      }
      // Reset page to 1 when sorting
      setPageIndex(firstPageIndex || 0)
      setSortConfig({key, direction: newDirection})
      handleSorting({column: key, direction: newDirection})
    },
    [sortConfig, handleSorting]
  )

  /**
   * Handle select/deselect all rows
   */
  const handleSelectAll = useCallback(() => {
    const allCurrentPageSelected = records.every(record => {
      return Array.from(globallySelectedRows).some(selected => getRowId(selected) === getRowId(record));
    });

    if (allCurrentPageSelected || globalSelectAll || partialSelectAll) {
      // Clear all selections when deselecting
      setSelectedRows([]);
      setGloballySelectedRows(new Set());
      setSelectAllTotal(0);
      setGlobalSelectAll(false);
      setPartialSelectAll(false);
      setDeselectedItems(new Set());
      onRowSelect([], false);
      setAllFilteredSelected(false);
    } else {
      // Add current page records to globally selected rows
      setGloballySelectedRows(prev => {
        const updated = new Set(prev);
        records.forEach(record => updated.add(record));
        return updated;
      });

      if (hasActiveFilters){
        setGlobalSelectAll(false);
        setAllFilteredSelected(true);
      } else {
        setGlobalSelectAll(true);
      }

      setSelectedRows(records);
      setSelectAllTotal(totalRecords);
      setPartialSelectAll(false);
      setDeselectedItems(new Set());
      onRowSelect(records, true);

    }
  }, [records, globallySelectedRows, onRowSelect, globalSelectAll, partialSelectAll, hasActiveFilters])

  /**
   * Handle single row selection toggle
   * @param {object} rowObj
   */
  const handleRowSelect = useCallback(
    rowObj => {
      if (!rowObj) return
      const identifier = getRowId(rowObj)
      if (!identifier) return

      const isSelected = selectedRows.some(row => {
        const rowId = getRowId(row)
        return rowId && rowId === identifier
      })

      let newSelection
      let newGloballySelectedRows;

      if (isSelected) {
        // If deselecting and we're in global select all or allFilteredSelected mode, switch to partial select all
        if (globalSelectAll || allFilteredSelected) {
          setGlobalSelectAll(false)
          setAllFilteredSelected(false)
          setPartialSelectAll(true)
          const newDeselectedItems = new Set(deselectedItems)
          newDeselectedItems.add(identifier)
          setDeselectedItems(newDeselectedItems)
        } else if (partialSelectAll) {
          // Add to deselected items if in partial select all mode
          const newDeselectedItems = new Set(deselectedItems)
          newDeselectedItems.add(identifier)
          setDeselectedItems(newDeselectedItems)
        }

        newGloballySelectedRows = new Set(globallySelectedRows);
        newGloballySelectedRows.forEach(row => {
          if (getRowId(row) === identifier) {
            newGloballySelectedRows.delete(row);
          }
        });
        setGloballySelectedRows(newGloballySelectedRows);

        newSelection = prev => prev.filter(row => getRowId(row) !== identifier)

      } else {
        // If selecting and we're in partial select all mode, remove from deselected items
        if (partialSelectAll) {
          const newDeselectedItems = new Set(deselectedItems)
          newDeselectedItems.delete(identifier)
          setDeselectedItems(newDeselectedItems)
        }

        newGloballySelectedRows = new Set(globallySelectedRows);
        newGloballySelectedRows.add(rowObj);
        setGloballySelectedRows(newGloballySelectedRows);


        newSelection = prev => [...prev, rowObj]
      }

      setSelectedRows(newSelection)
      onRowSelect(Array.from(newGloballySelectedRows), globalSelectAll || partialSelectAll || allFilteredSelected)
    },
    [selectedRows, globallySelectedRows, onRowSelect, globalSelectAll, partialSelectAll, allFilteredSelected, deselectedItems]
  )

  // Handle page change - triggers new server request
  /**
   * Handle page change
   * @param {object} event
   * @param {number} value
   */

  /**
   * Handle filter text input (debounced)
   * @param {object} e
   */
  const handleFilterChange = useCallback(
    e => {
      if (!e?.target) return
      const {name, value} = e.target

      if (!name) return

      setFilter(prev => ({...prev, [name]: value ?? ''}))

      // Reset selections when filter changes
      handleResetSelections()

      // Debounce search API call
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        const searchObj = {
          field: name,
          value: value ?? '',
          type: 'text',
        }
        setSearchArry(searchObj)
      }, 350)

      // Reset page to 1 when filtering
      setPageIndex(firstPageIndex || 0)
    },
    [setSearchArry, setPageIndex, handleResetSelections]
  )

  // Ensure selected rows persist and reflect the UI state after filter/page change
  useEffect(() => {
    // Skip this effect if selection persistence is disabled
    if (disableSelectionPersistence) {
      return;
    }

    if (globalSelectAll || allFilteredSelected) {
      return;
    }

    if (partialSelectAll) {
      setSelectedRows(
        records.filter(record => !deselectedItems.has(getRowId(record)))
      );
    } else if (globallySelectedRows.size > 0) {
      setSelectedRows(
        records.filter(record =>
          Array.from(globallySelectedRows).some(selected => getRowId(selected) === getRowId(record))
        )
      );
    }
  }, [records, globallySelectedRows, globalSelectAll, allFilteredSelected, partialSelectAll, deselectedItems, disableSelectionPersistence])


  /**
   * Handle date range filter (debounced)
   * @param {array} dates
   * @param {string} dateField
   */
  const handleDateRangeChange = useCallback(
    (dates, dateField) => {
      if (!dateField) return
      const [start, end] = dates || [null, null]

      // Always update the local date range state for UI
      setDateRanges(prev => ({
        ...prev,
        [dateField]: {startDate: start, endDate: end},
      }))

      // Reset selections when filter changes
      handleResetSelections()

      // Only proceed with API call if both dates are selected
      if (start && end) {
        // Create dates at midnight UTC to avoid timezone issues
        const startUTC = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate(), 0, 0, 0, 0))

        const endUTC = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59, 999))

        // Debounce API call for date range
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => {
          const searchObj = {
            field: dateField,
            value: {
              from: startUTC.toISOString(),
              to: endUTC.toISOString(),
            },
            type: 'dateRange',
          }
          setSearchArry(searchObj)
          setPageIndex(firstPageIndex || 0)
        }, 350)
      } else if (start === null && end === null) {
        const searchObj = {
          field: dateField,
          value: {
            from: '',
            to: '',
          },
          type: 'dateRange',
        }
        // Call API with the search object
        setSearchArry(searchObj)
        setPageIndex(firstPageIndex || 0)
      }
      // Don't call setSearchArry if not both dates are present
    },
    [setSearchArry, handleResetSelections]
  )

  /**
   * Handle single date filter (debounced)
   * @param {Date} date
   * @param {string} dateField
   */
  const handleSingleDateChange = useCallback(
    (date, dateField) => {
      if (!dateField) return

      // Update the local state for UI
      setSingleDates(prev => ({
        ...prev,
        [dateField]: date,
      }))

      // Reset selections when filter changes
      handleResetSelections()

      let formattedDate = ''
      if (date) {
        // Create date at midnight UTC to avoid timezone issues
        const dateUTC = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0))
        formattedDate = dateUTC.toISOString()
      }

      const searchObj = {
        field: dateField,
        value: formattedDate || '',
        type: 'date',
      }

      // Debounce API call for single date
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        setSearchArry(searchObj)
        setPageIndex(firstPageIndex || 0)
      }, 350)
    },
    [setSearchArry, handleResetSelections]
  )

  // Initial data load
  useEffect(() => {
    setSearchArry([])
  }, []) // Empty dependency array means this runs once on mount

  return {
    dateRanges,
    singleDates,
    openDatePicker,
    selectedRows,
    globallySelectedRows,
    allFilteredSelected,
    globalSelectAll,
    selectAllTotal,
    sortConfig,
    filter,
    totalPages,
    partialSelectAll,
    deselectedItems,
    setOpenDatePicker,
    hasActiveFilters,
    handleResetAll,
    handleResetFilters,
    handleResetSelections,
    handleSort,
    handleSelectAll,
    handleRowSelect,
    handleFilterChange,
    handleDateRangeChange,
    handleSingleDateChange,
    // No page or handlePageChange returned
  }
}
